/**
 * Lógica pura del calendario mensual de "Programación de Horarios" (sin DI, sin efectos
 * secundarios). El componente del diálogo solo orquesta: llama a estas funciones y pinta
 * el resultado. Ver dialog-work-schedule-calendar.component.ts.
 *
 * Un WorkShiftConfiguration real (api/WorkShift/Project/{id}) ya puede tener horas distintas
 * por día de la semana (o directamente no tener entrada ese día == descanso), así que cada
 * turno configurado en el proyecto se representa aquí como un "patrón semanal" completo de
 * 7 días, en vez de una sola franja horaria uniforme.
 */

import { WorkShiftConfiguration, WorkShiftSchedule } from '../interfaces/interfaces';
import { extractHHmm, extractMinutesFromApiTime, isZeroDurationRange } from './time-format.util';
import {
  RotationAssignment,
  ScheduleSource,
  WorkShiftRotation,
  mapRotationAssignmentFromApi
} from './work-shift-rotation.util';

export interface ResolvedDayShift {
  patternId: string;
  patternName: string;
  timeRangeLabel: string;
  durationHours: number;
  colorKey: string;
  isRest?: boolean;
  isRescheduled?: boolean;
  /** De qué capa viene el día: ajuste manual (INDIVIDUAL), rotación del contrato (ROTATION) o
   * turno del contrato (CONTRACT). Sin valor = día recién asignado en pantalla (aún sin guardar),
   * que se trata como INDIVIDUAL. Solo los días que NO son ROTATION se envían al guardar. */
  source?: ScheduleSource;
  rotationName?: string;
  /** Día del ciclo (0…N−1) cuando source = ROTATION. */
  cycleDay?: number;
}

export interface WeeklyPatternDay {
  /** Letra corta para el mini-preview del patrón: E=Estándar M=Mañana T=Tarde N=Nocturno S=Corta D=Descanso */
  letter: string;
  colorKey: string;
  shift: ResolvedDayShift | null; // null = descanso ese día
}

export interface WeeklyPattern {
  id: string;
  name: string;
  colorKey: string;
  /** 7 días, Lunes(0)...Domingo(6) */
  days: WeeklyPatternDay[];
  totalHours: number;
  workDaysCount: number;
  restDaysCount: number;
  /** true = turno diario (WorkShift.Daily.IsDaily) — se pinta como chip suelto en "TURNOS",
   * igual que en "Programación masiva de horarios", no como tarjeta de patrón semanal. */
  isDaily: boolean;
}

export interface CalendarDayCell {
  date: Date | null;
  dayNumber: number | null;
  inMonth: boolean;
  isWeekend: boolean;
  isHoliday: boolean;
}

export interface WeekTotal {
  totalHours: number;
  exceeds: boolean;
}

/** Lunes=0 ... Domingo=6, en el orden en que se pintan las columnas del calendario. */
const WEEKDAY_ORDER_MON_FIRST = [1, 2, 3, 4, 5, 6, 0];
const MONTH_ABBR = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Ley Emiliani: si el festivo no cae en lunes, se traslada al lunes siguiente. */
function toNextMonday(date: Date): Date {
  return date.getDay() === 1 ? date : addDays(date, (8 - date.getDay()) % 7);
}

/** Domingo de Pascua (algoritmo de Meeus/Jones/Butcher). */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=marzo, 4=abril
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Los 18 festivos oficiales de Colombia para un año dado, ya con el corrimiento de Ley Emiliani
 * aplicado a los que aplica. Solo para resaltar días — no reemplaza una fuente de festivos del
 * backend si en algún momento existiera una. Misma lógica que "Programación masiva de horarios"
 * (work-schedule-bulk.util.ts), que reusa esta función para que ambas pantallas coincidan. */
function getColombianHolidays(year: number): Set<string> {
  const easter = easterSunday(year);
  const dates: Date[] = [
    new Date(year, 0, 1),   // Año Nuevo
    new Date(year, 4, 1),   // Día del Trabajo
    new Date(year, 6, 20),  // Independencia
    new Date(year, 7, 7),   // Batalla de Boyacá
    new Date(year, 11, 8),  // Inmaculada Concepción
    new Date(year, 11, 25), // Navidad
    toNextMonday(new Date(year, 0, 6)),   // Reyes Magos
    toNextMonday(new Date(year, 2, 19)),  // San José
    toNextMonday(addDays(easter, 39)),    // Ascensión del Señor
    toNextMonday(addDays(easter, 60)),    // Corpus Christi
    toNextMonday(addDays(easter, 68)),    // Sagrado Corazón de Jesús
    toNextMonday(new Date(year, 5, 29)),  // San Pedro y San Pablo
    toNextMonday(new Date(year, 7, 15)),  // Asunción de la Virgen
    toNextMonday(new Date(year, 9, 12)),  // Día de la Raza
    toNextMonday(new Date(year, 10, 1)),  // Todos los Santos
    toNextMonday(new Date(year, 10, 11)), // Independencia de Cartagena
    addDays(easter, -3), // Jueves Santo
    addDays(easter, -2)  // Viernes Santo
  ];
  return new Set(dates.map(d => dateKey(d)));
}

const holidaysByYear = new Map<number, Set<string>>();

export function isColombianHoliday(date: Date): boolean {
  const year = date.getFullYear();
  if (!holidaysByYear.has(year)) { holidaysByYear.set(year, getColombianHolidays(year)); }
  return holidaysByYear.get(year).has(dateKey(date));
}

/**
 * Construye las semanas (Lun-Dom) de un mes. Las celdas antes del día 1 y después del último
 * día (para completar semanas de 7 columnas) llevan la fecha real del mes adyacente —
 * `inMonth: false` sólo para pintarlas distinto — porque una semana calendario no respeta el
 * corte de mes: si el lunes de la semana cae en el mes anterior (o el domingo en el siguiente),
 * ese día sigue siendo programable igual que cualquier otro (ver isCellLocked, que es el único
 * candado real).
 * @param month 1-12
 */
export function buildMonthWeeks(year: number, month: number): CalendarDayCell[][] {
  const firstOfMonth = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstColumnIndex = WEEKDAY_ORDER_MON_FIRST.indexOf(firstOfMonth.getDay());

  const buildCell = (date: Date, dayNumber: number, inMonth: boolean): CalendarDayCell => {
    const weekday = date.getDay();
    return {
      date,
      dayNumber,
      inMonth,
      isWeekend: weekday === 0 || weekday === 6,
      isHoliday: isColombianHoliday(date)
    };
  };

  const cells: CalendarDayCell[] = [];
  for (let i = firstColumnIndex; i > 0; i--) {
    const date = new Date(year, month - 1, 1 - i);
    cells.push(buildCell(date, date.getDate(), false));
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month - 1, day);
    cells.push(buildCell(date, day, true));
  }
  let trailingDay = 1;
  while (cells.length % 7 !== 0) {
    const date = new Date(year, month, trailingDay);
    cells.push(buildCell(date, date.getDate(), false));
    trailingDay++;
  }

  const weeks: CalendarDayCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

/** Clave estable "YYYY-MM-DD" en hora local, para usar como llave del mapa de asignaciones. */
export function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Rango de días en-mes de una semana, ej. "1 – 2 ago" o "31 – 31 ago". Vacío si la semana no tiene días en-mes. */
export function weekRangeLabel(week: CalendarDayCell[]): string {
  const inMonth = week.filter(c => c.inMonth && c.date);
  if (!inMonth.length) { return ''; }
  const first = inMonth[0].date as Date;
  const last = inMonth[inMonth.length - 1].date as Date;
  const monthAbbr = MONTH_ABBR[first.getMonth()];
  if (first.getDate() === last.getDate()) { return `${first.getDate()} ${monthAbbr}`; }
  return `${first.getDate()} – ${last.getDate()} ${monthAbbr}`;
}

export function computeWeekTotals(
  weeks: CalendarDayCell[][],
  assignments: Map<string, ResolvedDayShift | null>,
  maxWeeklyHours: number
): WeekTotal[] {
  return weeks.map(week => {
    const totalHours = week.reduce((sum, cell) => {
      if (!cell.date) { return sum; }
      const shift = assignments.get(dateKey(cell.date));
      return sum + (shift?.durationHours || 0);
    }, 0);
    return {
      totalHours: Math.round(totalHours * 10) / 10,
      exceeds: maxWeeklyHours > 0 && totalHours > maxWeeklyHours
    };
  });
}

function baseLetterFromName(name: string): string {
  if (/noc/i.test(name)) { return 'N'; }
  if (/ma[ñn]ana/i.test(name)) { return 'M'; }
  if (/tarde/i.test(name)) { return 'T'; }
  return 'E';
}

function mostCommonDuration(values: number[]): number | null {
  if (!values.length) { return null; }
  const counts = new Map<number, number>();
  values.forEach(v => counts.set(v, (counts.get(v) || 0) + 1));
  let best = values[0];
  let bestCount = 0;
  counts.forEach((count, v) => { if (count > bestCount) { bestCount = count; best = v; } });
  return best;
}

/**
 * Mapea un WorkShiftConfiguration real a un patrón semanal completo de 7 días. La letra de
 * cada día se deriva del nombre del turno (mañana/tarde/nocturno → M/T/N, si no calza → E), y
 * cualquier día cuya duración sea menor que la duración más frecuente del propio patrón se
 * marca como jornada corta ('S') — así "Obra L-V + sábado" sale E E E E E S D sin necesidad de
 * inventar reglas por nombre del día.
 */
export function buildWeeklyPatternFromWorkShift(w: WorkShiftConfiguration, colorKey: string): WeeklyPattern {
  const byIsoWeekday = new Map<number, WorkShiftSchedule>(); // 1=Lun...7=Dom
  (w.Schedule || []).forEach(s => {
    const iso = s.Day === 0 ? 7 : s.Day; // WorkShiftSchedule.Day: 0=Dom..6=Sáb
    byIsoWeekday.set(iso, s);
  });

  const workingDurations = Array.from(byIsoWeekday.values())
    .filter(s => !isZeroDurationRange(s.StartHour, s.EndHour))
    .map(s => extractMinutesFromApiTime(s.WorkingTime) / 60)
    .filter(h => h > 0);
  const modeDuration = mostCommonDuration(workingDurations);
  const baseLetter = baseLetterFromName(w.Name);

  const days: WeeklyPatternDay[] = [];
  let totalHours = 0;
  let workDaysCount = 0;

  for (let iso = 1; iso <= 7; iso++) {
    const entry = byIsoWeekday.get(iso);
    if (!entry || isZeroDurationRange(entry.StartHour, entry.EndHour)) {
      // Sin entrada en el patrón ese día de la semana (o con horario "00:00 a 00:00", que algunos
      // turnos usan como forma rara de decir "sin jornada") = descanso, pero el WorkShiftId a
      // enviar al guardar sigue siendo el del patrón: el backend resuelve por día según su propio
      // Schedule (ver api-programacion-horarios.md, sección 8).
      days.push({
        letter: 'D',
        colorKey: 'gray',
        shift: { patternId: w.Id, patternName: w.Name, timeRangeLabel: 'Descanso', durationHours: 0, colorKey: 'gray', isRest: true }
      });
      continue;
    }
    const durationHours = Math.round((extractMinutesFromApiTime(entry.WorkingTime) / 60) * 10) / 10;
    const isShortOutlier = !!modeDuration && durationHours > 0 && durationHours < modeDuration;
    const letter = isShortOutlier ? 'S' : baseLetter;
    days.push({
      letter,
      colorKey,
      shift: {
        patternId: w.Id,
        patternName: w.Name,
        timeRangeLabel: `${extractHHmm(entry.StartHour)} – ${extractHHmm(entry.EndHour)}`,
        durationHours,
        colorKey
      }
    });
    totalHours += durationHours;
    workDaysCount++;
  }

  return {
    id: w.Id,
    name: w.Name,
    colorKey,
    days,
    totalHours: Math.round(totalHours * 10) / 10,
    workDaysCount,
    restDaysCount: 7 - workDaysCount,
    isDaily: !!w.Daily?.IsDaily
  };
}

/**
 * Tipos y mapeo de la respuesta real de `api/PersonalReport/Schedule` (ver
 * api-programacion-horarios.md). GET, PUT y DELETE devuelven el mismo shape en éxito (200).
 */
export interface ScheduleWarningApi {
  Code: string;
  Message: string;
  Dates: string[];
}

export interface ScheduleDayApi {
  Date: string;
  WorkShiftId: string | null;
  WorkShiftName: string | null;
  EntryTime: string;
  LeaveTime: string;
  WorkedHours: string;
  IsRest: boolean;
  IsUnscheduled: boolean;
  IsRescheduled: boolean;
  /** Aditivos de rotaciones: nulo en contratos sin rotación ni capa individual. */
  Source?: ScheduleSource | null;
  RotationId?: string | null;
  RotationName?: string | null;
  RotationVersion?: number | null;
  CycleDay?: number | null;
}

export interface ScheduleResponseApi {
  PersonalEmployerId: number;
  From: string;
  To: string;
  EditableFrom: string | null;
  Days: ScheduleDayApi[];
  Warnings?: ScheduleWarningApi[];
  /** Asignaciones de rotación que tocan el rango pedido. */
  Rotations?: any[];
}

export interface MappedSchedule {
  assignments: Map<string, ResolvedDayShift>;
  editableFrom: Date | null;
  warnings: ScheduleWarningApi[];
  rotations: RotationAssignment[];
}

/**
 * Mapea la respuesta de GET/PUT/DELETE al modelo interno del calendario. Un día `IsUnscheduled`
 * queda sin entrada en el mapa de asignaciones (celda vacía = "sin turno vigente").
 */
export function mapScheduleResponse(resp: ScheduleResponseApi, patterns: WeeklyPattern[]): MappedSchedule {
  const assignments = new Map<string, ResolvedDayShift>();
  (resp.Days || []).forEach(d => {
    if (d.IsUnscheduled) { return; }
    // Un descanso de rotación llega con IsRest = true y WorkShiftId nulo: la persona SÍ cuenta
    // ese día (descanso), a diferencia de un día "sin programar".
    const isRotationRest = d.Source === 'ROTATION' && d.IsRest && !d.WorkShiftId;
    if (!d.WorkShiftId && !isRotationRest) { return; }
    const pattern = d.WorkShiftId ? patterns.find(p => p.id === d.WorkShiftId) : undefined;
    const colorKey = pattern?.colorKey || 'gray';
    // Un día "00:00 a 00:00" es, en la práctica, sin jornada — igual que IsRest — así que no debe
    // sumar horas aunque WorkedHours traiga algo raro (ver isZeroDurationRange).
    const isRest = d.IsRest || isZeroDurationRange(d.EntryTime, d.LeaveTime);
    assignments.set(d.Date.substring(0, 10), {
      patternId: d.WorkShiftId || '',
      patternName: isRotationRest
        ? (d.WorkShiftName || (d.RotationName ? `${d.RotationName} (descanso)` : 'Descanso'))
        : (d.WorkShiftName || pattern?.name || 'Turno'),
      timeRangeLabel: isRest ? 'Descanso' : `${extractHHmm(d.EntryTime)} – ${extractHHmm(d.LeaveTime)}`,
      durationHours: isRest ? 0 : Math.round((extractMinutesFromApiTime(d.WorkedHours) / 60) * 10) / 10,
      colorKey,
      isRest,
      isRescheduled: d.IsRescheduled,
      source: d.Source || undefined,
      rotationName: d.RotationName || undefined,
      cycleDay: d.CycleDay ?? undefined
    });
  });
  return {
    assignments,
    editableFrom: resp.EditableFrom ? new Date(resp.EditableFrom) : null,
    warnings: resp.Warnings || [],
    rotations: (resp.Rotations || []).map(mapRotationAssignmentFromApi)
  };
}

/**
 * Rotación del catálogo lista para pintar como tarjeta en el sidebar del calendario: cada día del
 * ciclo se resuelve contra los turnos del proyecto (color y horas), igual que un patrón semanal.
 */
export interface RotationPattern {
  id: string;
  name: string;
  versionNumber: number;
  cycleLengthDays: number;
  /** Fecha fin del patrón ("YYYY-MM-DD"); nulo = "Repetir indefinidamente" (sin límite de tiempo). */
  endDate: string | null;
  /** N días, indexados por día del ciclo (0…N−1). */
  days: WeeklyPatternDay[];
  totalHours: number;
  workDaysCount: number;
  restDaysCount: number;
}

export function buildRotationPattern(rotation: WorkShiftRotation, patterns: WeeklyPattern[]): RotationPattern {
  const days: WeeklyPatternDay[] = [];
  let totalHours = 0;
  let workDaysCount = 0;

  rotation.days.forEach(day => {
    const turno = day.workShiftId ? patterns.find(p => p.id === day.workShiftId) : undefined;
    const workingDay = turno?.days.find(d => d.shift && !d.shift.isRest);
    if (day.isRest || !day.workShiftId || !workingDay?.shift) {
      days.push({ letter: 'D', colorKey: 'gray', shift: null });
      return;
    }
    days.push({ letter: workingDay.letter, colorKey: turno.colorKey, shift: workingDay.shift });
    totalHours += workingDay.shift.durationHours;
    workDaysCount++;
  });

  return {
    id: rotation.id,
    name: rotation.name,
    versionNumber: rotation.versionNumber,
    cycleLengthDays: rotation.cycleLengthDays,
    endDate: rotation.endDate,
    days,
    totalHours: Math.round(totalHours * 10) / 10,
    workDaysCount,
    restDaysCount: days.length - workDaysCount
  };
}
